import {
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { wasMutationSuccessful } from '../../../../utils/mutations';
import { Textarea } from '../../../forms/Inputs';

import {
  type SimpleMessageFormCoachFragment,
  type SimpleMessageFormMessageCoachMutationVariables,
  useSimpleMessageFormMessageCoachMutation,
} from './__generated-gql-types__/SimpleMessageForm.generated';

const REGISTER_OPTIONS = Object.freeze({
  MESSAGE: {
    required: {
      value: true,
      message: 'Message is required',
    },
  },
});

interface SimpleMessageFormProps {
  coach: SimpleMessageFormCoachFragment;
  defaultMessage: string;
  onSuccess?: () => void;
  onRequestClose: () => void;
}

export const SimpleMessageForm: React.FC<SimpleMessageFormProps> = ({
  coach,
  defaultMessage,
  onSuccess,
  onRequestClose,
}) => {
  const { showToast } = useToast();

  const relationshipId = coach.coachingRelationship?.id ?? '';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SimpleMessageFormMessageCoachMutationVariables>({
    defaultValues: {
      relationshipId,
      message: defaultMessage,
    },
  });

  const [sendMessage] = useSimpleMessageFormMessageCoachMutation();

  const onSubmitWrapper: SubmitHandler<
    SimpleMessageFormMessageCoachMutationVariables
  > = async (values) => {
    const res = await sendMessage({
      variables: {
        relationshipId,
        message: values.message,
      },
    });

    if (!wasMutationSuccessful(res)) {
      showToast({
        type: ToastType.ERROR,
        duration: ToastDuration.NORMAL,
        message: "Couldn't send your message. Please try again.",
      });
      console.warn(res.errors);
      throw new Error('Failed to send message');
    } else {
      onSuccess?.();
      onRequestClose();
      showToast({
        type: ToastType.SUCCESS,
        duration: ToastDuration.NORMAL,
        message: 'Message sent.',
      });
    }
  };

  return (
    <form id="simple-message-form" onSubmit={handleSubmit(onSubmitWrapper)}>
      <Textarea
        label="Customize your message below"
        placeholder={defaultMessage}
        register={register('message', REGISTER_OPTIONS.MESSAGE)}
        rows={4}
        error={errors.message}
      />
    </form>
  );
};
